/**
 * Disk Image API service.
 * Provides CRUD operations for disk images.
 *
 * API Endpoints:
 * - GET /diskimages - List all disk images
 * - GET /diskimages/:name - Get disk image by name
 * - PUT /diskimages - Create or update disk image
 * - DELETE /diskimages/:name - Delete disk image
 * - GET /public/diskimages - List all public disk images
 * - GET /public/diskimages/:name - Get public disk image by name
 * - PUT /public/diskimages - Create public disk image (admin only)
 * - DELETE /public/diskimages/:name - Delete public disk image (admin only)
 *
 * @module services/diskImageService
 */

import { httpClient } from './httpClient';
import type {
  DiskImageRequest,
  DiskImageResponse,
  PublicDiskImageResponse,
  BuildDiskImageFromDockerfileRequest,
} from '@/types';

const ENDPOINT = '/diskimages';
const PUBLIC_ENDPOINT = '/public/diskimages';

/**
 * Disk Image Service
 * Encapsulates all disk image API operations
 */
export const diskImageService = {
  /**
   * List all disk images
   * @returns Promise<DiskImageResponse[]> - Array of disk images
   */
  async list(): Promise<DiskImageResponse[]> {
    console.log('[DiskImageService] Fetching all disk images...');
    try {
      const response = await httpClient.get<DiskImageResponse[]>(ENDPOINT);
      console.log('[DiskImageService] Fetched', response.data.length, 'disk images');
      return response.data;
    } catch (error) {
      console.error('[DiskImageService] Failed to fetch disk images:', error);
      throw error;
    }
  },

  /**
   * Get a disk image by ID
   * @param id - The ID of the disk image
   * @param sandboxGroupId - Optional sandbox group ID for group-scoped disk images
   * @returns Promise<DiskImageResponse> - The disk image
   */
  async getById(id: string, sandboxGroupId?: string): Promise<DiskImageResponse> {
    console.log(
      '[DiskImageService] Fetching disk image:',
      id,
      sandboxGroupId ? `(group: ${sandboxGroupId})` : ''
    );
    const encodedId = encodeURIComponent(id);
    try {
      const endpoint = sandboxGroupId
        ? `/sandboxGroups/${encodeURIComponent(sandboxGroupId)}/diskimages/${encodedId}`
        : `${ENDPOINT}/${encodedId}`;
      const response = await httpClient.get<DiskImageResponse>(endpoint);
      console.log('[DiskImageService] Fetched disk image:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[DiskImageService] Failed to fetch disk image:', id, error);
      throw error;
    }
  },

  /**
   * Create or update a disk image
   * @param request - The disk image request payload
   * @param sandboxGroupId - Optional sandbox group ID for group-scoped disk images
   * @returns Promise<DiskImageResponse> - The created/updated disk image
   */
  async create(request: DiskImageRequest, sandboxGroupId?: string): Promise<DiskImageResponse> {
    const endpoint = sandboxGroupId
      ? `/sandboxGroups/${encodeURIComponent(sandboxGroupId)}/diskimages`
      : ENDPOINT;
    console.log('[DiskImageService] Creating disk image:', request.labels?.['name'] || 'Unnamed');
    try {
      const response = await httpClient.put<DiskImageResponse>(endpoint, request);
      console.log('[DiskImageService] Created disk image:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[DiskImageService] Failed to create disk image:', error);
      throw error;
    }
  },

  /**
   * Delete a disk image by ID
   * @param id - The ID of the disk image to delete
   * @returns Promise<void>
   */
  async delete(id: string): Promise<void> {
    console.log('[DiskImageService] Deleting disk image:', id);
    const encodedId = encodeURIComponent(id);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedId}`);
      console.log('[DiskImageService] Deleted disk image:', id);
    } catch (error) {
      console.error('[DiskImageService] Failed to delete disk image:', id, error);
      throw error;
    }
  },

  /**
   * List all public disk images
   * @returns Promise<PublicDiskImageResponse[]> - Array of public disk images
   */
  async listPublic(): Promise<PublicDiskImageResponse[]> {
    console.log('[DiskImageService] Fetching all public disk images...');
    try {
      const response = await httpClient.get<PublicDiskImageResponse[]>(PUBLIC_ENDPOINT);
      console.log('[DiskImageService] Fetched', response.data.length, 'public disk images');
      return response.data;
    } catch (error) {
      console.error('[DiskImageService] Failed to fetch public disk images:', error);
      throw error;
    }
  },

  /**
   * Get a public disk image by ID
   * @param id - The ID of the public disk image
   * @returns Promise<DiskImageResponse> - The public disk image
   */
  async getPublicById(id: string): Promise<DiskImageResponse> {
    console.log('[DiskImageService] Fetching public disk image:', id);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.get<DiskImageResponse>(`${PUBLIC_ENDPOINT}/${encodedId}`);
      console.log('[DiskImageService] Fetched public disk image:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[DiskImageService] Failed to fetch public disk image:', id, error);
      throw error;
    }
  },

  /**
   * Create a public disk image (admin only)
   * @param request - The disk image request payload
   * @returns Promise<DiskImageResponse> - The created public disk image
   */
  async createPublic(request: DiskImageRequest): Promise<DiskImageResponse> {
    console.log(
      '[DiskImageService] Creating public disk image:',
      request.labels?.['name'] || 'Unnamed'
    );
    try {
      const response = await httpClient.put<DiskImageResponse>(PUBLIC_ENDPOINT, request);
      console.log('[DiskImageService] Created public disk image:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[DiskImageService] Failed to create public disk image:', error);
      throw error;
    }
  },

  /**
   * Delete a public disk image by ID (admin only)
   * @param id - The ID of the public disk image to delete
   * @returns Promise<void>
   */
  async deletePublic(id: string): Promise<void> {
    console.log('[DiskImageService] Deleting public disk image:', id);
    const encodedId = encodeURIComponent(id);
    try {
      await httpClient.delete(`${PUBLIC_ENDPOINT}/${encodedId}`);
      console.log('[DiskImageService] Deleted public disk image:', id);
    } catch (error) {
      console.error('[DiskImageService] Failed to delete public disk image:', id, error);
      throw error;
    }
  },

  /**
   * List disk images for a specific sandbox group
   * @param sandboxGroupId - The sandbox group ID
   * @returns Promise<DiskImageResponse[]> - Array of disk images in the group
   */
  async listForGroup(sandboxGroupId: string): Promise<DiskImageResponse[]> {
    const encoded = encodeURIComponent(sandboxGroupId);
    try {
      const response = await httpClient.get<DiskImageResponse[]>(
        `/sandboxGroups/${encoded}/diskimages`
      );
      return response.data;
    } catch (error) {
      console.error('[DiskImageService] Failed to fetch group disk images:', sandboxGroupId, error);
      throw error;
    }
  },

  /**
   * Build a disk image from a Dockerfile
   * @param request - The Dockerfile build request payload
   * @param sandboxGroupId - Optional sandbox group ID for group-scoped disk images
   * @returns Promise<DiskImageResponse> - The created disk image
   */
  async buildFromDockerfile(
    request: BuildDiskImageFromDockerfileRequest,
    sandboxGroupId?: string
  ): Promise<DiskImageResponse> {
    const endpoint = sandboxGroupId
      ? `/sandboxGroups/${encodeURIComponent(sandboxGroupId)}/diskimages/dockerfile`
      : `${ENDPOINT}/dockerfile`;
    console.log('[DiskImageService] Building disk image from Dockerfile');
    try {
      const response = await httpClient.post<DiskImageResponse>(endpoint, request);
      console.log('[DiskImageService] Built disk image from Dockerfile:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[DiskImageService] Failed to build disk image from Dockerfile:', error);
      throw error;
    }
  },
};
